import MainWrapper from "@components/Container/MainWrapper";
import { CompanionAdsImu2 } from "@elements/Advertisement/variant/CompanionAdImu";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import fetchTablaApi from "@helper/fetchTablaApi";
import { capitalize } from "@helper/stringTransform";
import { formatDate } from "@helper/useDateFormat";
import {
  getCookieByName,
  getWindowOrigin,
  isArrayWithElements,
  isClientSide,
} from "@helper/utils";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useVisibleArticle from "@hooks/useVisibleArticle";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import ArticleContentCard from "@routes/Article/ArticleContentCard";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { AdvertisementTypeEnum, PageAdTargetValue } from "@typings/Ads.d";
import {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import React, { ReactElement, useEffect, useState } from "react";

type ArticleInfiniteScrollProps = {
  mainArticle: ProcessedArticleData;
  pageAdTargetValue: PageAdTargetValue;
  onNewArticle: (
    article: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => void;
  gsChannels?: string[];
  isCuePreview?: boolean;
};

export type ArticleVariantData = {
  payload: {
    article: {
      variant: string;
    };
  };
};

export default function ArticleInfiniteScroll({
  mainArticle,
  pageAdTargetValue,
  onNewArticle,
}: ArticleInfiniteScrollProps): ReactElement {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [newArticleGaLoaded, setNewArticleGaLoaded] = useState<boolean>(false);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>([mainArticle.id]),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [visibleArticleId, setVisibleArticleId] = useState<string>(
    mainArticle.id,
  );
  const [articleHeight, setArticleHeight] = useState<number>(0);
  const { isScreenLG } = useWindowSize();

  // Trigger useVisibleArticle when LoadMoreData changes
  const visibleArticleIdFromHook = useVisibleArticle(visibleArticleId);
  const [currentPath, setCurrentPath] = useState<string>(
    mainArticle.urlPath || "",
  );
  const [latestArticle, setLatestArticle] = useState<ProcessedArticleData>();
  const TablaCustomGaEvent = useTablaCustomEventAnalyticsCall();

  let filterArticlesBasedOn = "";
  if (mainArticle.subSectionUniqName) {
    filterArticlesBasedOn = mainArticle.subSectionUniqName;
  } else {
    filterArticlesBasedOn = mainArticle.sectionUniqName
      ? mainArticle.sectionUniqName
      : "";
  }

  const fetchNextStoryData = async (
    requestData: MoreArticlesRequestData,
  ): Promise<ProcessedArticleData[]> => {
    const queryParams = new URLSearchParams({
      section: requestData.filterArticlesBasedOn as string,
      excludeIds: requestData.excludeIds as string,
    }).toString();
    const response: ProcessedArticleData[] = await fetchTablaApi(
      `get-article?${queryParams}`,
      "GET",
    );
    return response;
  };

  const origin = getWindowOrigin();

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? mainArticle : loadMoreData.at(-1);

    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn,
      excludeIds: [...renderedArticleIds].join(","),
    };

    const fetchFunction: FetchMoreArticlesFunction = fetchNextStoryData;

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: 1,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);

    if (isArrayWithElements(response)) {
      const article = response[0];
      setVisibleArticleId(article.id);
      onNewArticle(article, pageNumber + 1, false);
      setLatestArticle(article);
    } else {
      setHasMoreStories(false);
    }

    return response;
  };

  useEffect(() => {
    if (latestArticle && newArticleGaLoaded) {
      const mysphw = getCookieByName("mysphw");
      const visitor_cat = getCookieByName("visitorcat");
      const user_svc = getCookieByName("svc");
      TablaCustomGaEvent({
        event: "virtual_pv",
        articleid: latestArticle.id,
        author: latestArticle.authors
          ? latestArticle.authors.map((author) => author.name).join(", ")
          : "",
        contentType: "article",
        cue_articleid: latestArticle.id,
        keyword: latestArticle.tags
          ? latestArticle.tags.map((tags) => tags.name).join(", ")
          : "",
        title: latestArticle.title,
        url: `${origin}${latestArticle.urlPath}`,
        pubdate: formatDate(latestArticle.createdDate, "yyyy-MM-dd HH:mm:ss"),
        cd_version: "tabla2",
        svc: user_svc !== undefined ? user_svc : "",
        contentcat: 1,
        at: mysphw != undefined ? mysphw : "",
        visitorcat: visitor_cat != undefined ? visitor_cat : 1,
        pagination: pageNumber == 0 ? 1 : pageNumber,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newArticleGaLoaded]);

  useEffect(() => {
    if (!isClientSide || !visibleArticleId) return;

    const allArticles = [mainArticle, ...loadMoreData];
    const visibleArticleIndex = allArticles.findIndex(
      (article) => article.id === visibleArticleId,
    );

    const articleData =
      visibleArticleIndex !== -1 ? allArticles[visibleArticleIndex] : undefined;
    if (articleData) {
      const newPath = articleData.urlPath;
      if (newPath && newPath !== currentPath) {
        window.history.pushState("article", articleData.title, newPath);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCurrentPath(newPath || "");
        // Update Browser title
        document.title = `${articleData.title}, ${capitalize(articleData.sectionName)}, Tabla`;
        onNewArticle(articleData, visibleArticleIndex, true);

        const articleElement = document.getElementById(visibleArticleId);
        if (articleElement) {
          setArticleHeight(articleElement.offsetHeight);
        }
      }
    }
  }, [visibleArticleId, loadMoreData, mainArticle, onNewArticle, currentPath]);

  useEffect(() => {
    if (!isClientSide) return;
    if (visibleArticleIdFromHook) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisibleArticleId(visibleArticleIdFromHook);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNewArticleGaLoaded(true);
    }
  }, [visibleArticleIdFromHook]);

  return (
    <>
      {[...loadMoreData].map((context, index) => (
        <React.Fragment key={index}>
          <div id={context.id} className="article-card xmd:mt-10">
            <CompanionAdsLb
              adType={AdvertisementTypeEnum.LB2}
              uniqueSectionName={
                context.subSection
                  ? `${context.sectionName}/${context.subSection}`
                  : context.sectionName
              }
              index={index + 1}
              pageAdTargetType={pageAdTargetValue}
              articleSlotTarget={[
                {
                  key: "page_number",
                  value: (index + 2).toString(),
                },
                {
                  key: "articleid",
                  value: context.id,
                },
                {
                  key: "tags",
                  value:
                    context.tags?.map((tags) => tags.name).join(", ") || "",
                },
              ]}
              rootClassName={"mb-10 relative pt-xm pb-5"}
            />
            <MainWrapper className="hover:text flex flex-col lg:flex-row justify-between xmd:gap-10 py-3 xmd:p-0">
              <ArticleContentCard
                articleData={context}
                pageNumber={index + 1}
                isInfiniteScrollArticle={true}
                articleSlotTarget={[
                  { key: "page_number", value: (index + 1).toString() },
                  {
                    key: "articleid",
                    value: context.id,
                  },
                  {
                    key: "tags",
                    value:
                      context.tags?.map((tags) => tags.name).join(", ") || "",
                  },
                ]}
                enableReadMore={true}
              />
              {isScreenLG && (
                <div
                  className={`lg:w-4/12  lg:min-h-[${articleHeight}px] relative justify-center flex`}
                >
                  <CompanionAdsImu2
                    uniqueSectionName={
                      context.subSection
                        ? `${context.sectionName}/${context.subSection}`
                        : context.sectionName
                    }
                    pageAdTargetType={pageAdTargetValue}
                    pageNumber={index + 1}
                    rootClassName={
                      "xmd:absolute xmd:top-0 xmd:bottom-10 xmd:!h-auto mx-auto w-fit"
                    }
                    articleSlotTarget={[
                      {
                        key: "page_number",
                        value: (index + 2).toString(),
                      },
                      {
                        key: "articleid",
                        value: context.id,
                      },
                      {
                        key: "tags",
                        value:
                          context.tags?.map((tags) => tags.name).join(", ") ||
                          "",
                      },
                    ]}
                  />
                </div>
              )}
            </MainWrapper>
          </div>
        </React.Fragment>
      ))}
      <LoadMore
        rootClassName="my-4"
        onLoadMore={handleLoadMore}
        hasMore={hasMoreStories}
        loadDefaultGaEvent={false}
        landingPage={`${origin}${mainArticle.urlPath}`}
        mobileInfiniteScroll={true}
        isArticle={true}
      />
    </>
  );
}
